import { useEffect } from "react";

const SPORTS360_URL = "https://app.sports360.ph/sportshub/assenda";

/**
 * Same-origin intermediate page for the "OP" external link.
 *
 * Why this exists: Sports360 sits behind Cloudflare Bot Management, which
 * blocks cross-site navigations carrying a non-empty `Referer` header from
 * our preview/production domains. We solve it by:
 *   1) Setting `<meta name="referrer" content="no-referrer">` so this page
 *      itself sends no Referer when navigating away.
 *   2) Triggering the redirect from a same-origin context — the link in the
 *      Assenda header now opens this page in a new tab (so popup blockers
 *      stay happy), and this page then navigates the same tab to Sports360.
 *
 * If the auto-redirect is somehow blocked, the user still sees a clear
 * "Continue to Sports360" button as a manual fallback.
 */
const OpenPlaysRedirect = () => {
  useEffect(() => {
    // Strip any referrer for the outgoing navigation.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);

    // Tiny delay so the user sees the intermediate page (and any pre-existing
    // tab-blockers have already resolved on the previous user click).
    const t = setTimeout(() => {
      window.location.replace(SPORTS360_URL);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="op-redirect-page"
      className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white px-6"
    >
      <div className="card-surface p-8 max-w-md text-center border-2 border-white/10 rounded-md">
        <div className="inline-block w-10 h-10 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin mb-6"></div>
        <h1 className="text-xl font-black mb-2">Heading to Sports360…</h1>
        <p className="text-sm text-[#a1a1aa] mb-6 leading-snug">
          You're leaving Assenda to view Open Plays on Sports360.
          If you aren't redirected automatically in a moment, tap the button
          below.
        </p>
        <a
          href={SPORTS360_URL}
          rel="noopener noreferrer external"
          referrerPolicy="no-referrer"
          data-testid="op-redirect-continue"
          className="inline-block w-full px-4 py-2.5 bg-[#8cc63f] text-black font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-[#9ed15c] transition-colors"
        >
          Continue to Sports360
        </a>
        <p className="text-[11px] text-[#a1a1aa] mt-5">
          <a
            href="/"
            className="hover:text-[#8cc63f]"
            data-testid="op-redirect-back"
          >
            ← Back to Assenda
          </a>
        </p>
      </div>
    </div>
  );
};

export default OpenPlaysRedirect;
